<template>
  <div class="pa-4 pa-md-6">
    <div class="grey--text" style="font-size: 13px">Otso Panalo Game</div>
    <h2 class="font-weight-bold mb-3 mb-md-5">Games</h2>

    <v-card class="shadow">
      <v-card-title>
        <v-row justify="space-between">
          <v-col cols="12" sm="6" md="4" xl="3" align-self="end">
            <CreateGame
              v-if="accountInfo.access && accountInfo.access.includes(32)"
              class="mr-1"
            />

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="secondary"
                  v-bind="attrs"
                  v-on="on"
                  @click="loadRecords"
                >
                  <v-icon>mdi-cached</v-icon>
                </v-btn>
              </template>
              <span>Refresh</span>
            </v-tooltip>
          </v-col>

          <v-col cols="12" sm="6" md="4" xl="3">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              placeholder="Search"
              single-line
              hide-details="auto"
              :disabled="loading"
              clearable
              outlined
              dense
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="records"
          :search="search"
          :loading="loading"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          loading-text="Loading, please wait a moment"
          :items-per-page="10"
        >
          <template v-slot:[`item.action`]="{ item }">
            <v-tooltip right>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  small
                  color="primary"
                  @click="showDetails(item.id)"
                >
                  <v-icon size="large">mdi-text-box-search-outline</v-icon>
                </v-btn>
              </template>
              <span>Details</span>
            </v-tooltip>
          </template>

          <template v-slot:[`item.game`]="{ item }">
            <div>
              <div class="font-weight-medium">{{ item.league }}</div>
              <div>
                {{ item.team_a_name }} {{ item.team_a_alias }}
                <span class="text-overline my-2 mx-2 font-italic">vs</span>
                {{ item.team_b_name }} {{ item.team_b_alias }}
              </div>
            </div>
          </template>

          <template v-slot:[`item.schedule`]="{ item }">
            <span>{{ formatTheDate(item.schedule) }}</span>
          </template>

          <template v-slot:[`item.betting_deadline`]="{ item }">
            <span>{{
              item.betting_deadline ? formatTheDate(item.betting_deadline) : "-"
            }}</span>
          </template>

          <template v-slot:[`item.betting`]="{ item }">
            <v-chip
              small
              :class="
                item.betting === 'Enabled'
                  ? 'green lighten-5 green--text'
                  : 'orange lighten-5 orange--text'
              "
            >
              {{ item.betting }}
            </v-chip>
          </template>

          <template v-slot:[`item.game_status`]="{ item }">
            <v-chip small :class="gameStatusChipClass(item.game_status)">
              {{ item.game_status }}
            </v-chip>
          </template>

          <template v-slot:[`item.bet_amount`]="{ item }">
            <span>{{ moneyFormat(item.bet_amount) }}</span>
          </template>

          <template v-slot:[`item.jackpot_prize`]="{ item }">
            <span>{{ moneyFormat(item.jackpot_prize) }}</span>
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            <span>{{ formatTheDate(item.created_at) }}</span>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <Snackbar ref="snackbar" />
  </div>
</template>

<script>
import { mapState } from "vuex";
import formatDate from "@/helpers/formatDate";
import responseGet from "@/helpers/api_request_get";
import Snackbar from "@/components/Snackbar";
import CreateGame from "@/components/otso_panalo/CreateGame";

export default {
  components: {
    CreateGame,
    Snackbar,
  },

  data: () => ({
    loading: false,
    search: "",
    sortBy: "created_at",
    sortDesc: true,
    records: [],
    headers: [
      {
        text: "Action",
        value: "action",
        class: "text-uppercase",
        sortable: false,
        align: "center",
      },
      { text: "ID", value: "id", class: "text-uppercase" },
      { text: "Game", value: "game", class: "text-uppercase", sortable: false },
      { text: "Schedule", value: "schedule", class: "text-uppercase" },
      {
        text: "Betting Deadline",
        value: "betting_deadline",
        class: "text-uppercase",
      },
      { text: "Betting", value: "betting", class: "text-uppercase" },
      { text: "Status", value: "game_status", class: "text-uppercase" },
      { text: "Bet Amount", value: "bet_amount", class: "text-uppercase" },
      {
        text: "Jackpot Prize",
        value: "jackpot_prize",
        class: "text-uppercase",
      },
      { text: "Created At", value: "created_at", class: "text-uppercase" },
    ],
  }),

  computed: {
    ...mapState(["accountInfo"]),
  },

  created() {
    this.loadRecords();

    if (this.$route.query.created === "1") {
      this.$nextTick(() => {
        this.$refs.snackbar.show(
          "success",
          "mdi-check-circle-outline",
          "Otso Panalo game created successfully.",
          4000,
        );
      });

      this.$router.replace({ name: "OtsoPanaloGames" });
    }

    if (this.$route.query.updated === "1") {
      this.$nextTick(() => {
        this.$refs.snackbar.show(
          "success",
          "mdi-check-circle-outline",
          "Otso Panalo game updated successfully.",
          4000,
        );
      });

      this.$router.replace({ name: "OtsoPanaloGames" });
    }
  },

  methods: {
    formatTheDate(value) {
      return formatDate(value, true);
    },

    moneyFormat(value) {
      const amount = Number(value || 0);

      return `PHP ${amount.toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },

    gameStatusChipClass(status) {
      if (status === "Cancelled") {
        return "red lighten-5 red--text";
      }

      if (status === "Finished") {
        return "green lighten-5 green--text";
      }

      return "blue lighten-5 blue--text text--darken-2";
    },

    async loadRecords() {
      try {
        this.loading = true;

        const response = await responseGet("", "/otso_panalo/games");

        if (response.status === 200) {
          this.records = response.data.records || [];
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.$store.commit(
            "setRefreshError",
            "Failed to load Otso Panalo games.",
          );
        }
      } catch (error) {
        this.$store.commit(
          "setRefreshError",
          "Failed to load Otso Panalo games.",
        );
      } finally {
        this.loading = false;
      }
    },

    showDetails(id) {
      this.$router.push({
        name: "OtsoPanaloGameDetails",
        params: { id },
      });
    },
  },
};
</script>
